import { GameState } from './GameState';
import { GameData } from '../data/GameData';
import { GameConfig } from '../core/GameConfig';
import { EventBus, GameEvents } from '../core/EventBus';
import { rankSystem } from './RankSystem';
import { fanSystem } from './FanSystem';
import { NormalNpc, NpcGreeting, ScriptDef } from '../data/types';
import { PERSONALITY_KEYS, PersonalityKey } from '../data/tables/NormalNpcData';
import { GUEST_ASK_ME, GUEST_ASK_NPC, GUEST_INVITE_OPEN } from '../data/tables/EncounterTexts';

/**
 * 好友客串（设计=docs/好友客串设计.md）两条通道：
 * 方向二（§一，玩家邀好友客串自己的戏）：入口在人脉聊天页选项清单，本系统管资格判定与原子结算——
 *   合格单 = active 合同 ∧ 自己一番/二番 ∧ 客串Σ未撞顶；结算=扣好感阶梯 + 抬客串账本 + 好友沾光涨名气。
 * 方向一（§二，好友月初邀玩家客串好友的戏）：rollInvites() 月初掷 20% 出邀请入招呼队列，
 *   接受=蹭名气+好感（acceptEffects，由 GreetingSystem.respondInvite 在玩家抉择时调用），婉拒=0。
 * ⛔ 每剧月限（2026-09-28 用户撤销，好感阶梯=唯一闸门）；好友名气收益 ⛔ 上屏（用户口令）。
 */

/** 聊天页「邀客串」二级列表项（结构性合格的戏都列出来；每位好友不可请的挂置灰原因，点击弹提示） */
export interface GuestContractRow {
    contractId: string;
    scriptId: string;
    scriptName: string;
    /** null=可请；非 null=该行置灰原因（⛔ 成交，点击弹提示） */
    disabledReason: string | null;
}

/** 结算结果（供 UI 拼气泡与结果行） */
export interface GuestSettleResult {
    scriptName: string;
    /** 实扣好感（= 该好友咖位阶梯值） */
    favorCost: number;
    /** 本剧客串账本增量（钳到剩余封顶后 ≥1） */
    heatGain: number;
    /** 好友沾光名气（⛔ 上屏，仅日志） */
    friendFameGain: number;
    meText: string;
    npcText: string;
}

function pick(arr: string[]): string {
    return arr[Math.floor(Math.random() * arr.length)];
}

/** NPC 性格键（命中四模板用之，否则 special 通用池）；客串/剧宣两系统的台词分池共用 */
export function npcPersonalityKey(npc: NormalNpc): PersonalityKey | 'special' {
    return (PERSONALITY_KEYS as string[]).includes(npc.personality)
        ? npc.personality as PersonalityKey : 'special';
}

/**
 * 客串/好友剧宣共用的**软顶**收益（2026-10-28 B+C 案）：两通道共池 Σ 越接近 底数×GUEST_HEAT_CAP_MULT，
 * 收益按 fade=1−Σ/cap 线性衰减；Σ=cap 后收益 0——但⛔ 锁邀请：请求仍成立、好感照扣（上限只是防通胀，
 * 不做玩家面前的墙）。返回值带 max(1,·) 保底：低咖位好友仍是 +1（存量口径），趋顶由 fade 自然压薄。
 */
export function softLedgerHeatGain(script: ScriptDef, ledgerSum: number, rawHeat: number): number {
    const cap = script.heat * GameConfig.GUEST_HEAT_CAP_MULT;
    const remain = Math.round(cap) - ledgerSum;
    if (remain <= 0) return 0;
    const fade = Math.max(0, 1 - ledgerSum / cap);
    return Math.max(1, Math.min(Math.round(rawHeat * fade), remain));
}

export class GuestCameoSystem {
    private static instance: GuestCameoSystem;

    private constructor() { }

    static getInstance(): GuestCameoSystem {
        if (!this.instance) {
            this.instance = new GuestCameoSystem();
        }
        return this.instance;
    }

    /** 请客失败/入口不可用的原因（null=可请）：好感门槛先行，其次手里有没有戏 */
    gate(npcId: string): string | null {
        const state = GameState.getInstance();
        if (!state.player.normalNpcs.some(n => n.id === npcId) || GameData.NPC_MAP[npcId]) return null; // 特殊 NPC 无客串语义（含竞争型实例），交由调用方不挂行
        const favor = state.getFavor(npcId);
        if (favor < GameConfig.GUEST_MIN_FAVOR)
            return `好感度不足：需 ≥${GameConfig.GUEST_MIN_FAVOR}，当前 ${favor}`;
        return null;
    }

    /** 一次请托的固定好感消耗（按好友咖位阶梯，供入口提示与结算同源） */
    favorCost(npc: NormalNpc): number {
        return GameConfig.GUEST_FAVOR_COSTS[Math.max(1, Math.min(npc.rank, 10)) - 1];
    }

    /** 二级列表全部行：拍摄期合同 ∧ 自己一番/二番（结构性合格的戏一律列出，⛔ 因热度软顶锁行——B+C 案 2026-10-28）；
     *  该好友已在此剧客串过 / 已在此剧有戏 → 保留该行但挂 disabledReason（置灰可点弹原因，2026-09-30 用户改案）。 */
    contractRows(npcId: string): GuestContractRow[] {
        const state = GameState.getInstance();
        const npc = state.player.normalNpcs.find(n => n.id === npcId);
        const rows: GuestContractRow[] = [];
        for (const c of state.player.contracts) {
            if (c.status !== 'active') continue;
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script) continue;
            const billing = script.roles[c.roleIndex]?.billing;
            if (billing !== 1 && billing !== 2) continue;   // 只有一、二番才开得了这个口
            let disabledReason: string | null = null;
            if (npc && (npc.activeJobs ?? []).some(j => j.scriptId === script.id)) {
                disabledReason = `TA 已在《${script.name}》剧组中有戏份，无需重复客串`;
            } else if ((state.player.guestedBy?.[script.id] ?? []).includes(npcId)) {
                disabledReason = `TA 已在《${script.name}》客串过，同一作品仅可邀请一次`;
            }
            rows.push({ contractId: c.id, scriptId: script.id, scriptName: script.name, disabledReason });
        }
        return rows;
    }

    /** 可请单 = contractRows 去掉置灰行（结算侧硬校验同源） */
    eligibleContracts(npcId: string): GuestContractRow[] {
        return this.contractRows(npcId).filter(r => !r.disabledReason);
    }

    /** 结构性无单说明（置灰行已进列表，此处只在「一戏不剩」时兜底；各档各说各的因） */
    emptyReason(npcId: string): string {
        const state = GameState.getInstance();
        const active = state.player.contracts.filter(c => c.status === 'active' && GameData.SCRIPT_MAP[c.scriptId]);
        if (active.length === 0) return '当前没有在拍的作品';
        const lead = active.filter(c => {
            const billing = GameData.SCRIPT_MAP[c.scriptId].roles[c.roleIndex]?.billing;
            return billing === 1 || billing === 2;
        });
        if (lead.length === 0) return '在拍作品中没有你的一番/二番角色，不满足邀请条件';
        return '当前没有可邀请的作品';   // 理论不可达（热度软顶不锁行），兜底防 undefined
    }

    /** 原子结算：扣好感 → 抬客串账本（钳剩余封顶）→ 好友沾光；余额不足 ⛔ 成行（好感 ⛔ 扣成负） */
    settle(npcId: string, contractId: string): GuestSettleResult | null {
        const state = GameState.getInstance();
        const npc = state.player.normalNpcs.find(n => n.id === npcId);
        const contract = state.player.contracts.find(c => c.id === contractId && c.status === 'active');
        if (!npc || !contract) return null;
        const row = this.eligibleContracts(npcId).find(r => r.contractId === contractId);
        if (!row) return null;
        const script = GameData.SCRIPT_MAP[row.scriptId];
        const favorCost = this.favorCost(npc);
        if (state.getFavor(npcId) < favorCost) return null;

        const workHeat = fanSystem.workHeatOf(script);
        // 软顶（B+C 案）：共池 Σ 越满收益越薄，触顶收益 0 但请求仍成立
        const ledgerSum = (state.player.guestPromoted?.[script.id] ?? 0)
            + (state.player.friendPromoted?.[script.id] ?? 0);
        const raw = npc.fame * GameConfig.GUEST_HEAT_K
            * Math.min(1, GameConfig.GUEST_DECAY_ANCHOR / Math.max(1, workHeat));
        const heatGain = softLedgerHeatGain(script, ledgerSum, raw);

        state.addFavor(npcId, -favorCost);
        state.player.guestPromoted = {
            ...(state.player.guestPromoted ?? {}),
            [script.id]: (state.player.guestPromoted?.[script.id] ?? 0) + heatGain,
        };
        // 记「该好友已在此剧客串过」：同一部剧每个好友只请得动一次（一剧一签，二次开口=赖皮）
        state.player.guestedBy = {
            ...(state.player.guestedBy ?? {}),
            [script.id]: [...(state.player.guestedBy?.[script.id] ?? []), npcId],
        };
        const friendFameGain = Math.round(workHeat * GameConfig.GUEST_NPC_FAME_K);
        npc.fame += friendFameGain;
        npc.rank = rankSystem.getRankFromFame(npc.fame);

        const meText = pick(GUEST_ASK_ME).replace(/\{script\}/g, script.name);
        const npcText = pick(GUEST_ASK_NPC[this.personalityKey(npc)]).replace(/\{script\}/g, script.name);
        return { scriptName: script.name, favorCost, heatGain, friendFameGain, meText, npcText };
    }

    /** NPC 性格键（命中四模板用之，否则 special 通用池）；方向一/二好友台词分池共用 */
    private personalityKey(npc: NormalNpc): PersonalityKey | 'special' {
        return npcPersonalityKey(npc);
    }

    // ===== 方向一：好友月初邀玩家客串（§二） =====

    /** 合格被邀人：好感 ≥60（好友档，与方向二齐平）∧ 有戏在拍（activeJobs 含 shooting）∧ 该剧已下架（离开玩家剧本池=过了选角期、真进拍摄期，2026-09-29 用户定）∧ 玩家未参演该剧（2026-10-03 用户令：玩家已在该作有合同 ⇒ 同组同事，⛔ 再被邀去客串自己正在演的戏，方向二「有戏好友⛔ 客串」的镜像） */
    private inviteCandidates(): { npc: NormalNpc; scriptId: string; heat: number }[] {
        const state = GameState.getInstance();
        const out: { npc: NormalNpc; scriptId: string; heat: number }[] = [];
        for (const npc of state.player.normalNpcs) {
            if (state.getFavor(npc.id) < GameConfig.GUEST_MIN_FAVOR) continue;
            // ⛔ 选角期就能邀：NPC 季初派角即挂 shooting，但玩家还在剧本册试镜=该剧仍在 marketScripts；
            // 只有剧已下架（本季刷新掉、纯拍摄中）才算「签约后的拍摄期」，此时才请得动你客串
            // ⛔ 玩家已参演该剧（有合同，含在拍/已杀青）→ 该剧从这位好友的可邀 job 里剔除（好友若另有玩家没演的在拍剧仍可邀）
            const job = (npc.activeJobs ?? []).find(j =>
                j.phase === 'shooting' && GameData.SCRIPT_MAP[j.scriptId]
                && !state.player.marketScripts.includes(j.scriptId)
                && !state.player.contracts.some(c => c.scriptId === j.scriptId));
            if (!job) continue;   // 无在拍 / 播出期 / 仍在选角池 / 玩家已参演 → 都不算可邀
            out.push({ npc, scriptId: job.scriptId, heat: GameData.SCRIPT_MAP[job.scriptId].heat });
        }
        return out;
    }

    /**
     * 月初掷邀请（GameLoop 在 greetingSystem.roll() 之后调用，同窗挂在新月份）：
     * 有合格候选才掷，全局每月一次 GUEST_INVITE_PCT%，命中随机抽 1 人产出邀请入招呼队列（与招呼共用红点）。
     * 未抉择的邀请月末随 GreetingSystem.roll 的「未回应即过期」filter 一并作废（不跨月，§三 6 条）。
     */
    rollInvites(): void {
        const state = GameState.getInstance();
        const p = state.player;
        const cands = this.inviteCandidates();
        if (cands.length === 0) return;
        if (Math.random() * 100 >= GameConfig.GUEST_INVITE_PCT) return;
        const c = cands[Math.floor(Math.random() * cands.length)];
        const script = GameData.SCRIPT_MAP[c.scriptId];
        const key = this.personalityKey(c.npc);
        const text = pick(GUEST_INVITE_OPEN[key]).replace(/\{script\}/g, script.name);
        p.greetings.push({
            npcId: c.npc.id, text, year: p.year, month: p.month,
            read: false, replied: false,
            kind: 'guestInvite', inviteOptions: ['接受邀请', '婉拒'],
            guestScriptId: c.scriptId, guestHeat: c.heat,
        });
        if (p.greetings.length > GameConfig.GREETING_QUEUE_MAX) {
            p.greetings = p.greetings.slice(-GameConfig.GREETING_QUEUE_MAX);
        }
    }

    /** 接受邀请的结算（由 GreetingSystem.respondInvite 在 picked 首次落定时调用一次，幂等由调用方保证）：
     *  蹭名气 = max(1, round(好友剧底数 heat × GUEST_RIDE_K))（走 addFame 单点，即时抬粉一并生效）+ 好感 +3 */
    acceptEffects(g: NpcGreeting): { fameGain: number; favorGain: number } {
        const state = GameState.getInstance();
        const fameGain = Math.max(1, Math.round((g.guestHeat ?? 0) * GameConfig.GUEST_RIDE_K));
        const favorGain = GameConfig.GUEST_RIDE_FAVOR;
        state.addFame(fameGain);
        state.addFavor(g.npcId, favorGain);
        // 累计接受客串邀约计数（成就「盛情难却/友情出演」判定源；纯统计可补字段，缺省 0）
        state.player.totalGuestRides = (state.player.totalGuestRides ?? 0) + 1;
        return { fameGain, favorGain };
    }
}

function socialName(npcId: string): string {
    const special = GameData.NPC_MAP[npcId];
    if (special) return special.name;
    return GameState.getInstance().player.normalNpcs.find(n => n.id === npcId)?.name ?? npcId;
}

/** 导出单例 */
export const guestCameoSystem = GuestCameoSystem.getInstance();
